import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import AdminCheckbox from '../AdminCheckbox/AdminCheckbox';
import styles from './AdminMultiselect.module.css';

const formatChoice = (choice) => choice && choice.replace(/\w+/g, _.capitalize);

const CheckboxSection = ({ options, propRef, name, colorHexcodes }) => (
  <>
    {_.map(options, (choice) => {
      const { choiceId, choiceName } = choice;
      return (
        <AdminCheckbox
          label={formatChoice(choiceName)}
          value={choiceId}
          propRef={propRef}
          name={name}
          key={`${name}_${choiceId}`}
          id={`${name}_${choiceId}`}
          colorHexcode={colorHexcodes ? colorHexcodes[choiceId] : null}
        />
      );
    })}
  </>
);

const TagsSection = ({ toggleDropdown, selected, hash, setValue, name }) => {
  const handleDelete = (e, index) => {
    if (e.stopPropagation) e.stopPropagation();
    const newArr = [...selected];
    newArr.splice(index, 1);
    setValue(name, newArr);
  };
  return (
    <div
      className={styles.select}
      role="button"
      onKeyDown={toggleDropdown}
      onClick={toggleDropdown}
      tabIndex={0}
    >
      {selected && selected.length
        ? _.map(selected, (s, index) => (
            <div className={styles.tagButton} key={s}>
              {formatChoice(_.get(hash, `[${s}].choiceName`))}
              <button
                onClick={(e) => handleDelete(e, index)}
                type="button"
                className={`${styles.deleteButton} naked-button`}
              >
                <i className="far fa-times" />
              </button>
            </div>
          ))
        : 'Select'}
    </div>
  );
};

const AdminMultiselect = ({
  choices,
  propRef,
  name,
  label,
  style,
  selected,
  hash,
  type,
  setValue,
  colorHexcodes,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => setIsOpen((prevState) => !prevState);
  const [display, setDisplay] = useState('Select');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (selected && selected.length) {
      const displayName = _.join(
        _.map(selected, (v) => formatChoice(_.get(hash, `[${v}].choiceName`))),
        ', '
      );
      setDisplay(displayName);
    } else {
      setDisplay('Select');
    }
  }, [selected, hash]);

  useEffect(() => {
    if (searchQuery === '') {
      setOptions(choices);
    }
  }, [choices, searchQuery]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    const input = e.target.value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const reg = new RegExp(input, 'i'); // consecutive letters
    const filtered = [];
    const copy = _.cloneDeep(choices);
    _.forEach(copy, (choice) => {
      const { choiceName, children } = choice;
      if (reg.test(choiceName)) {
        return filtered.push(choice);
      }
      if (children) {
        choice.children = _.filter(children, (child) =>
          reg.test(child.choiceName)
        );
        if (choice.children.length) {
          filtered.push(choice);
        }
      }
    });
    setOptions(filtered);
  };
  return (
    <div className={styles.container} style={style}>
      <div className={styles.dropdownButtonContainer}>
        <span className={styles.label}>{label}</span>
        {type === 'tags' ? (
          <TagsSection
            hash={hash}
            selected={selected}
            toggleDropdown={toggleDropdown}
            setValue={setValue}
            name={name}
          />
        ) : (
          <button
            className={styles.select}
            type="button"
            onClick={toggleDropdown}
          >
            <span>{display}</span>
          </button>
        )}
      </div>
      <div
        className={`${styles.choiceContainer} ${isOpen ? styles.open : ''}`}
        id="multiselect-container"
        onMouseLeave={() => {
          setIsOpen(false);
          setSearchQuery('');
        }}
      >
        {(choices && choices.length > 10) ||
          (_.some(
            choices,
            (choice) => choice.children && choice.children.length
          ) && (
            <div className={styles.search}>
              <i className="far fa-search" />
              <input
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search"
              />
            </div>
          ))}
        <div className={styles.checkboxContainer}>
          {_.map(options, (option) => {
            const { choiceId, choiceName, children } = option;
            if (children) {
              return (
                <React.Fragment key={`${name}_${choiceId}`}>
                  <p className={styles.category}>{choiceName}</p>
                  <CheckboxSection
                    options={children}
                    propRef={propRef}
                    name={name}
                    colorHexcodes={colorHexcodes}
                  />
                </React.Fragment>
              );
            }
            return (
              <AdminCheckbox
                label={formatChoice(choiceName)}
                value={choiceId}
                propRef={propRef}
                name={name}
                key={`${name}_${choiceId}`}
                id={`${name}_${choiceId}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminMultiselect;
